import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCompleteMetaOAuth } from '../api/hooks';
import { ErrorBanner } from '../components/ui';

const SETTINGS_PATH = '/settings/integrations/meta';

/**
 * Where Facebook returns after login.
 *
 * The authorization code is single-use and short-lived, so the exchange is
 * fired exactly once — the ref guard matters because React's StrictMode runs
 * effects twice in development, and the second attempt would fail against a
 * code Meta has already burned, showing an error for a login that worked.
 */
export function MetaOAuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const complete = useCompleteMetaOAuth();
  const started = useRef(false);
  const [error, setError] = useState<unknown>(null);

  const code = params.get('code');
  const state = params.get('state');
  // Set when the admin pressed Cancel, or Facebook refused the request.
  const denied = params.get('error_description') ?? params.get('error');

  useEffect(() => {
    if (started.current || denied || !code || !state) return;
    started.current = true;

    complete
      .mutateAsync({ code, state })
      .then(() => navigate(SETTINGS_PATH, { replace: true }))
      .catch(setError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, state, denied]);

  const failed = denied ?? (!code || !state ? 'Facebook did not return an authorization code.' : null);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Connecting to Facebook</h1>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {failed ? (
            <>
              <div className="banner banner-error">{failed}</div>
              <Link className="btn btn-primary" to={SETTINGS_PATH}>
                Back to Meta settings
              </Link>
            </>
          ) : error ? (
            <>
              <ErrorBanner error={error} />
              <p className="hint">
                The login itself may have succeeded — check the connection status before trying
                again, since an authorization code can only be used once.
              </p>
              <Link className="btn btn-primary" to={SETTINGS_PATH}>
                Back to Meta settings
              </Link>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="spinner" />
              <span>Exchanging the login with Meta…</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
